/**
 * Practice navigation (docs/spec/07 §2): set tabs, a 5-wide grid of caves (scrolling for the
 * player's own), the level, PLAY. Pure: a state and the action in, the next state out.
 */

import type { MenuAction } from '../scene';
import { scrollToShow } from '../kit/menuModel';

export type PracticeTab = 'classic' | 'deep' | 'mine';
export const PRACTICE_TABS: readonly { readonly id: PracticeTab; readonly label: string }[] = [
  { id: 'classic', label: 'CLASSIC' },
  { id: 'deep', label: 'DEEP' },
  { id: 'mine', label: 'MY CAVES' },
];

export const GRID_COLS = 5;
export const GRID_ROWS = 4;

export type Focus = 'tabs' | 'grid' | 'level' | 'play';

export interface PracticeState {
  readonly tab: number;
  readonly index: number;
  readonly level: number;
  readonly focus: Focus;
  /** First visible grid row. */
  readonly scroll: number;
}

export const START_STATE: PracticeState = { tab: 0, index: 0, level: 1, focus: 'grid', scroll: 0 };

/** What an action did besides moving: nothing, a tab change, a play, or a refusal. */
export type PracticeEffect = 'none' | 'moved' | 'tab' | 'play' | 'blocked';

/** Keep the selected cave's row on screen. */
export function withScroll(s: PracticeState, count: number): PracticeState {
  const rows = Math.ceil(count / GRID_COLS);
  return { ...s, scroll: scrollToShow(Math.floor(s.index / GRID_COLS), s.scroll, GRID_ROWS, rows) };
}

/** What an action led to. */
export interface PracticeStep {
  readonly state: PracticeState;
  readonly effect: PracticeEffect;
}

/** The next state after `a` with `count` caves in the current tab (`tabs` is how many tabs there are). */
export function practiceNav(s: PracticeState, a: MenuAction, count: number, tabs = PRACTICE_TABS.length): PracticeStep {
  const done = (state: PracticeState, effect: PracticeEffect = 'moved'): PracticeStep => ({
    state: withScroll(state, count),
    effect,
  });
  const blocked = { state: s, effect: 'blocked' as const };
  if (s.focus === 'tabs') {
    if (a === 'left' || a === 'right') {
      const tab = Math.max(0, Math.min(tabs - 1, s.tab + (a === 'left' ? -1 : 1)));
      return tab === s.tab ? blocked : { state: { ...s, tab, index: 0, scroll: 0 }, effect: 'tab' };
    }
    if (a === 'down' || a === 'confirm') return done({ ...s, focus: 'grid' });
    return a === 'up' ? blocked : { state: s, effect: 'none' };
  }
  if (s.focus === 'grid') {
    const col = s.index % GRID_COLS;
    const row = Math.floor(s.index / GRID_COLS);
    const lastRow = Math.max(0, Math.ceil(count / GRID_COLS) - 1);
    if (a === 'confirm') return count > 0 ? { state: s, effect: 'play' } : blocked;
    if (a === 'up') {
      return done(row === 0 || count === 0 ? { ...s, focus: 'tabs' } : { ...s, index: s.index - GRID_COLS });
    }
    if (a === 'down') {
      if (count === 0 || row >= lastRow) return done({ ...s, focus: 'level' });
      return done({ ...s, index: Math.min(count - 1, s.index + GRID_COLS) });
    }
    if (a === 'left') return col === 0 || count === 0 ? blocked : done({ ...s, index: s.index - 1 });
    if (a === 'right') {
      return col === GRID_COLS - 1 || s.index >= count - 1 ? blocked : done({ ...s, index: s.index + 1 });
    }
    return { state: s, effect: 'none' };
  }
  if (s.focus === 'level') {
    if (a === 'left' || a === 'right') {
      const level = Math.max(1, Math.min(5, s.level + (a === 'left' ? -1 : 1)));
      return level === s.level ? blocked : done({ ...s, level });
    }
    if (a === 'up') return done({ ...s, focus: 'grid' });
    if (a === 'down') return done({ ...s, focus: 'play' });
    if (a === 'confirm') return count > 0 ? { state: s, effect: 'play' } : blocked;
    return { state: s, effect: 'none' };
  }
  if (a === 'up') return done({ ...s, focus: 'level' });
  if (a === 'confirm') return count > 0 ? { state: s, effect: 'play' } : blocked;
  return { state: s, effect: 'none' };
}
